




















































































































































































































































import { Component, Prop, PropSync, Watch, Vue } from "vue-property-decorator";
import store from "@/store";
//  @utilities
import moment from "moment";
import numberHelper from "Utilities/number-helper";
import htmlEncoder from "Utilities/htmlencode-helper";
// @types
import { Company } from "@/types/company";
import { Feature } from "@/types/feature";
import { ExtendedVessel } from "@/types/vessel";
//  @clients
import UserService from "@/services/UserService";
import featuresClient from "Clients/features-client";
import vesselsAdministrationsClient from "Clients/vessels-administrations-client";
//  @modules
import { getModule } from "vuex-module-decorators";
import UserModule from "@/store/clients/User.module";
import SnackbarModule from "@/store/clients/Snackbar.module";
import { UserProfile } from "@/types/userProfile";

const User = getModule(UserModule, store);
const Snackbar = getModule(SnackbarModule, store);

@Component({
  components: {},
})
export default class UserDataModal extends Vue {
  @PropSync("value", { required: true, type: Boolean, default: false }) isModalActive!: boolean;
  @Prop({ type: Array, default: () => [], required: true }) users!: any[];
  @PropSync("userToEditId", { type: String, default: null }) syncUserToEditId!: string | null;
  @Prop({ type: String, default: "463px" }) width!: string;

  // @Data
  roleList: string[] = ["User", "SuperUser", "Administrator", "ApiUser", "KognifaiUser"];
  internalRoleSelected: string | null = "User"; // default role
  formData: Partial<UserProfile> = {};

  // @Watchers
  @Watch("isModalActive")
  onModalActiveChanged(isOpen: boolean): void {
    if (isOpen) this.initializeForm();
  }

  //  @Validations
  fieldRequired = {
    required: (value: any) => {
      // Allow 0 as valid value for numeric fields
      if (value === 0) return true;
      // Check if value exists (not null, undefined, or empty string)
      return !!value || "Field is required";
    },
    nameMaxLength: (value: any) => {
      // allow required() to handle empty - here validate length when present
      if (value === null || value === undefined || String(value).trim() === "") return true;
      return String(value).length <= 30 || "May not be longer than 30 characters";
    },
    titleMaxLength: (value: any) => {
      // allow required() to handle empty - here validate length when present
      if (value === null || value === undefined || String(value).trim() === "") return true;
      return String(value).length <= 50 || "May not be longer than 50 characters";
    },
    email: (value: any) => {
      if (value === null || value === undefined || String(value).trim() === "") return true;
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailPattern.test(String(value)) || "Invalid email format";
    },
  };

  // @Getters
  get isNewUser(): boolean {
    return this.syncUserToEditId == null;
  }

  get currentUserRole(): string | null {
    return User.userProfile ? User.userProfile.role : null;
  }

  get isUserSuperUserOrAdmin(): boolean {
    return this.currentUserRole === "SuperUser" || this.currentUserRole === "Administrator";
  }

  get ModalMode(): any {
    return {
      mode: this.isNewUser ? "add" : "edit",
      title: this.isNewUser ? "Add User" : "Edit User",
      btnText: this.isNewUser ? "Add User" : "Update",
    };
  }

  get isSubmitDisabled(): boolean {
    // Use bound formData properties (template uses formData.*)
    const name = this.formData.name ?? "";
    const role = this.formData.role ?? "";

    const v = this.fieldRequired;

    // validator returns true (valid) or an error string -> treat only true as valid
    const ok = (validator: Function, value: any) => {
      try {
        return validator(value) === true;
      } catch {
        return false;
      }
    };

    const nameOk = ok(v.required, name) && ok(v.nameMaxLength, name);
    const roleOk = ok(v.required, role);

    // For ApiUser, only name and role are required
    if (role === "ApiUser") {
      return !(nameOk && roleOk);
    }

    // For other roles, validate email and title as well
    const email = this.formData.email ?? "";
    const title = this.formData.title ?? "";
    const emailOk = ok(v.required, email) && ok(v.email, email);
    const titleOk = ok(v.required, title) && ok(v.titleMaxLength, title);

    return !(nameOk && emailOk && titleOk && roleOk);
  }

  // @Methods
  // Initialize form data based on whether we're creating or editing
  initializeForm(): void {
    if (this.isNewUser) {
      // New user: reset form with all fields initialized
      this.formData = {
        name: "",
        email: "",
        title: "",
        phoneNumber: "",
        role: "User",
      };
      this.internalRoleSelected = "User";

      // Reset all field validations for add mode
      this.$nextTick(() => {
        this.$nextTick(() => {
          const form = this.$el.querySelector(".dfi__form");
          if (form) {
            const textFields = form.querySelectorAll(".v-text-field");
            textFields.forEach((field: any) => {
              if (field.__vue__ && field.__vue__.resetValidation) {
                field.__vue__.resetValidation();
              }
            });
          }
        });
      });
    } else {
      // Edit mode: populate form with user data
      const user = this.users.find(u => u.id === this.syncUserToEditId);
      if (user) {
        // Clone user data and
        this.formData = {
          name: user.name || "",
          email: user.email || "",
          title: user.title || "",
          phoneNumber: user.phoneNumber || "",
          role: user.role || "User",
        };
        this.internalRoleSelected = user.role;
      }
    }
  }

  close(): void {
    this.isModalActive = false;
    this.$emit("update:userToEditId", null);
    // Also reset form when canceling
    setTimeout(() => {
      this.resetForm();
    }, 300);
  }

  cancel(): void {
    this.isModalActive = false;
    // Reset userToEditId in parent when canceling
    this.$emit("update:userToEditId", null);
    // Also reset form when canceling
    setTimeout(() => {
      this.resetForm();
    }, 300);
  }

  async submit(): Promise<void> {
    try {
      if (this.isNewUser) {
        await UserService.create(this.formData);
      } else {
        this.formData = {
          ...this.formData,
          id: this.syncUserToEditId!,
        };
        await UserService.update(this.formData);
      }
      this.$emit("onSubmit", { userData: this.formData, isNewUser: this.isNewUser });
      Snackbar.showSnackbar({
        text: `User <b>${htmlEncoder.htmlEncode(this.formData.name)}</b> successfully ${this.isNewUser ? "created" : "updated"}`,
        color: "success",
      });
    } catch (error) {
      Snackbar.showSnackbar({ text: "Failed to submit user data" });
    } finally {
      this.isModalActive = false;
      // Reset userToEditId in parent after submission
      this.$emit("update:userToEditId", null);
      // Also reset form after submission
      setTimeout(() => {
        this.resetForm();
      }, 300);
    }
  }

  onRoleChange(): void {
    // Handle any additional logic when role changes, if needed
  }

  // Reset form data and validations
  resetForm(): void {
    // Reset form data to initial state
    this.formData = { role: "User" };
    this.internalRoleSelected = "User";

    // Reset all field validations
    this.$nextTick(() => {
      const fields = ["emailField", "titleField", "phoneNumberField", "roleField"];
      fields.forEach(fieldRef => {
        const field = (this.$refs as any)[fieldRef];
        if (field && field.resetValidation) {
          field.resetValidation();
        }
      });

      // Also reset v-text-fields in the form
      const form = this.$el?.querySelector?.(".dfi__form");
      if (form) {
        const textFields = form.querySelectorAll(".v-text-field");
        textFields.forEach((field: any) => {
          if (field.__vue__ && field.__vue__.resetValidation) {
            field.__vue__.resetValidation();
          }
        });
      }
    });
  }

  numericFilter(event: any): any {
    return numberHelper.numericRoundFilter(event);
  }
}
